import { Component, computed, DestroyRef, inject, Signal } from '@angular/core';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { PlIcon } from '../icon-library/icons';
import { EntityStatus, Level, MenuModel } from '@shared/models/menu.model';
import { ParticipantProgression, ParticipantService } from '@shared/services/participant/participant.service';
import { ErrorService } from '@shared/services/error.service';
import { ZendeskService } from '@shared/services/zendesk.service';
import { Breakpoint, WindowEventService } from '@shared/services/window.event.service';
import { distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlRouter } from '@shared/services/pl-router.service';
import { getRoutePath, PlRoute } from '@app/utils/routes';

interface CycleProgress {
  title: string;
  subtitle: string;
  percentageCompletion: number;
  isLocked: boolean;
  isCurrent: boolean;
}

@Component({
  selector: 'pl-home',
  templateUrl: './home.component.html',
})
export class HomeComponent {
  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly PlIcon: typeof PlIcon = PlIcon;

  public participantFirstName: Signal<string> = computed(() => this.participantService.participant()?.first_name || '');

  public participantProgression: Signal<ParticipantProgression | null> = computed(() =>
    this.participantService.participantProgression(),
  );

  public cycleProgress: Signal<CycleProgress[]> = computed(() =>
    this.getParticipantCycleProgression(this.participantService.menu()),
  );

  public isBigScreen: boolean = true;

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private errorService: ErrorService,
    private participantService: ParticipantService,
    private plRouter: PlRouter,
    private windowEventService: WindowEventService,
    private zendeskService: ZendeskService,
  ) {
    this.windowEventService
      .getWindowResizeEvent()
      .pipe(distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((size: number): void => {
        this.isBigScreen = size > Breakpoint.sm;
      });
  }

  public goToCurrentLifeActs(linkNextLifeActs: string | undefined, event: MouseEvent): void {
    if (linkNextLifeActs) {
      this.plRouter.navigate([linkNextLifeActs], undefined, event);
    } else {
      throw new Error(`We should always have linkNextLifeActs: ${linkNextLifeActs}`);
    }
  }

  public goToLifeChanges(event: MouseEvent): void {
    this.plRouter.navigate([getRoutePath(PlRoute.lifeChanges)], undefined, event);
  }

  public goToNextStep(linkNextStep: string | undefined, event: MouseEvent): void {
    if (linkNextStep) {
      this.plRouter.navigate([linkNextStep], undefined, event);
    } else {
      this.participantService.toggleLeftMenu();
    }
  }

  public goToHelpAndFaq(): void {
    window.open('https://support.potentialife.com/hc/en-gb', '_blank');
  }

  public contactUs(): void {
    this.zendeskService.contactUs();
  }

  private getParticipantCycleProgression(menu: MenuModel | null): CycleProgress[] {
    if (menu) {
      const levels: Level[] = menu.menudata[0].children;
      return levels.map((level: Level, index: number): CycleProgress => {
        return {
          title: level.title,
          subtitle: level.name,
          percentageCompletion: +level.progress.percent,
          isLocked: level.status === EntityStatus.locked,
          isCurrent:
            level.status === EntityStatus.current ||
            (level.status === EntityStatus.completed && index === menu.menudata[0].children.length - 1),
        };
      });
    }

    return [];
  }
}
